import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, AlertTriangle, ArrowRight, Users as UsersIcon, Database } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AdminDiagnose() {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = () => {
    if (!q.trim()) return toast.error('Enter a name, email, user_id or referral code');
    setLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/admin/partners/diagnose?q=${encodeURIComponent(q.trim())}`, true);
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status === 200) setData(JSON.parse(xhr.responseText));
      else toast.error('Diagnose failed');
    };
    xhr.onerror = () => { setLoading(false); toast.error('Network error'); };
    xhr.send();
  };

  const reassign = (fromPid, toUid) => {
    if (!window.confirm(`Move ALL referrals and transactions from partner_id "${fromPid}" into user_id "${toUid}"? This cannot be undone automatically.`)) return;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/partners/reassign-referrals`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        const d = JSON.parse(xhr.responseText);
        toast.success(`Moved ${d.referrals_moved} referrals and ${d.transactions_moved} transactions to ${d.to_user?.name || d.to_user?.email}`);
        run();
      } else if (xhr.status === 403) {
        toast.error('Only super admins can reassign referrals');
      } else {
        toast.error('Reassign failed');
      }
    };
    xhr.send(JSON.stringify({ from_partner_id: fromPid, to_user_id: toUid }));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-6 h-6 text-orange-500" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Diagnose referral visibility</h1>
      </div>
      <p className="text-muted-foreground mb-4">
        Shows every user doc matching your query, their referral counts, merge chain, and orphan referrals whose partner_id no longer exists.
      </p>

      <Card className="p-4 mb-4">
        <div className="flex gap-2">
          <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} data-testid="diagnose-input"
            placeholder="Partner name, email, user_id, or referral code (e.g. Sameera / samdubai7869@gmail.com)" />
          <Button onClick={run} disabled={loading} data-testid="diagnose-run" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Search className="w-4 h-4 mr-1.5" /> {loading ? 'Running…' : 'Diagnose'}
          </Button>
        </div>
      </Card>

      {data && (
        <div className="space-y-4" data-testid="diagnose-results">
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="w-4 h-4" />
              <strong>Query:</strong> <code className="bg-muted px-1.5 py-0.5 rounded text-sm">{data.query}</code>
              <Badge variant="outline">{data.matched_user_count} user doc(s)</Badge>
            </div>
          </Card>

          {data.users.length === 0 && (
            <Card className="p-6 text-center text-muted-foreground">No users matched. Try an exact email or user_id.</Card>
          )}

          {data.users.map(u => (
            <Card key={u.user_id} className="p-4" data-testid={`user-${u.user_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-lg">{u.name || '—'}</strong>
                    <Badge variant="outline" className="font-mono text-xs">{u.user_id}</Badge>
                    {u.referral_code && <Badge className="bg-orange-100 text-orange-700 border-orange-200" variant="outline">{u.referral_code}</Badge>}
                    {u.status && <Badge variant="outline">{u.status}</Badge>}
                    {u.role && <Badge variant="outline">{u.role}</Badge>}
                    {u.cold && <Badge className="bg-gray-200 text-gray-800">cold</Badge>}
                    {u.not_interested_at && <Badge className="bg-red-100 text-red-700">not_interested</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {u.email || '—'} {u.phone && <> · 📞 {u.phone}</>}
                    {u.created_at && <> · created {new Date(u.created_at).toLocaleDateString()}</>}
                    {u.last_login && <> · last login {new Date(u.last_login).toLocaleDateString()}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{u.referrals_total_as_partner_id}</div>
                  <div className="text-xs text-muted-foreground">referrals under this user_id</div>
                  <div className="text-xs text-muted-foreground">{u.transactions_total_as_partner_id} txns</div>
                </div>
              </div>

              {u.merged_into && (
                <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded text-sm" data-testid={`merged-${u.user_id}`}>
                  <strong>🔗 Merged into chain:</strong> {u.user_id}
                  {u.merged_into_chain.map((hop, i) => (
                    <span key={i}> <ArrowRight className="w-3 h-3 inline mx-1" /> <code className="bg-white px-1 rounded">{hop}</code></span>
                  ))}
                  <p className="text-xs text-muted-foreground mt-1">When this partner logs in, the backend transparently resolves them to the final user_id in the chain. Any referrals left under this doc are "stuck".</p>
                </div>
              )}

              {u.merged_in_from && u.merged_in_from.length > 0 && (
                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
                  <strong>⬅ Other accounts merged INTO this one:</strong>
                  <ul className="mt-1 ml-4 list-disc">
                    {u.merged_in_from.map(m => (
                      <li key={m.user_id}>
                        <code className="bg-white px-1 rounded text-xs">{m.user_id}</code> — {m.name} ({m.email}) · status: {m.status}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {u.latest_referral && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Latest referral: <strong>{u.latest_referral.name}</strong> · {u.latest_referral.status} · {u.latest_referral.created_at && new Date(u.latest_referral.created_at).toLocaleDateString()}
                </div>
              )}
            </Card>
          ))}

          {data.orphan_partner_ids_total > 0 && (
            <Card className="p-4 border-amber-300 bg-amber-50" data-testid="orphan-section">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <strong>Orphan referrals detected</strong>
                <Badge className="bg-amber-200 text-amber-800">{data.orphan_partner_ids_total} dangling partner_id(s)</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                These referrals point to a <code>partner_id</code> that no longer exists in the users collection. If one of them matches this partner, reassign them to the canonical user_id.
              </p>
              <div className="space-y-2">
                {data.orphan_partner_ids.map(o => (
                  <div key={o.partner_id} className="flex items-center justify-between gap-3 bg-white p-2 rounded border">
                    <div className="text-sm">
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{o.partner_id}</code>
                      <span className="ml-2 text-muted-foreground">{o.count} referrals · latest {o.latest && new Date(o.latest).toLocaleDateString()}</span>
                    </div>
                    {data.users.length > 0 && (
                      <select
                        className="text-xs border rounded p-1"
                        onChange={e => e.target.value && reassign(o.partner_id, e.target.value)}
                        defaultValue=""
                        data-testid={`reassign-to-${o.partner_id}`}
                      >
                        <option value="">Reassign to…</option>
                        {data.users.map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.name || u.email} ({u.user_id.slice(0, 12)})</option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
